import React from 'react';
import PropTypes from 'prop-types';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCog,
  faInfo,
  faTag,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import AssetBasicInfo from './AssetBasicInfo';
import { AssetAdvancedInfo } from './AssetAdvancedInfo';
import AssetTagContainer from './AssetTagContainer';
import ModalLayout from '../ModalLayout';
import { UpdateTagsConsumer } from '../../Context/UpdateTagsContext';

export const EditAssetModalDisplay = ({
  asset,
  availableTemplates,
  commandAndControl,
  currentTemplateID,
  error,
  handleCCToggle,
  handlePropUpdate,
  handlePropUpdateSimple,
  handleTemplateSelect,
  handleTimezoneSelected,
  handleTimezoneToggle,
  isOpen,
  selectedTemplate,
  selectedTimezone,
  submitUpdate,
  templatesLoading,
  timezone,
  toggle,
  toggleView,
  updateRequestPending,
  stwWiFiControl,
  stwWiFiEnabled,
  WiFissid,
  WiFipassword,
  vendor,
  viewing,
}) => {
  return (
    <ModalLayout desktopHeight="50vh" isOpen={isOpen} toggleModal={toggle}>
      <ModalHeader toggle={toggle}>
        <span className="font-weight-medium text-text-dark">Edit Asset</span>
      </ModalHeader>
      <ModalBody className="py-0 row align-items-stretch">
        <div className="d-none d-lg-block col-lg-3 bg-background-gray pt-3 pl-2 modal-side-nav-container">
          <div className="mb-3">
            {viewing === 'basics' && (
              <div className="page-indicator position-absolute" />
            )}
            <button
              className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left side-nav text-decoration-none ${
                viewing === 'basics' && 'text-muted'
              }`}
              onClick={() => toggleView('basics')}
            >
              Basics
            </button>
          </div>
          <div className="mb-3">
            {viewing === 'advanced' && (
              <div className="page-indicator position-absolute" />
            )}
            <button
              className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left side-nav text-decoration-none ${
                viewing === 'advanced' && 'text-muted'
              }`}
              onClick={() => toggleView('advanced')}
            >
              Advanced
            </button>
          </div>
          <div className="mb-3">
            {viewing === 'tag' && (
              <div className="page-indicator position-absolute" />
            )}
            <button
              className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left side-nav text-decoration-none ${
                viewing === 'tag' && 'text-muted'
              }`}
              onClick={() => toggleView('tag')}
            >
              Tags
            </button>
          </div>
        </div>
        <div className="col-12 col-lg-9 bg-white p-3 p-lg-4 modal-main-content-container">
          <UpdateTagsConsumer>
            {(updateTags) => (
              <AssetTagContainer
                visibility={viewing === 'tag' ? '' : 'd-none'}
                asset={asset}
                updateTags={updateTags}
              />
            )}
          </UpdateTagsConsumer>
          <form
            className={`h-100 d-${
              viewing === 'tag' ? 'none' : 'flex'
            } flex-column`}
            onSubmit={submitUpdate}
          >
            <AssetBasicInfo
              availableTemplates={availableTemplates}
              commandAndControl={commandAndControl}
              currentTemplateID={currentTemplateID}
              customTag={'Custom tag'}
              handleCCToggle={handleCCToggle}
              handlePropUpdate={handlePropUpdate}
              handleTemplateSelect={handleTemplateSelect}
              handleTimezoneSelected={handleTimezoneSelected}
              handleTimezoneToggle={handleTimezoneToggle}
              nickname={asset.nickname}
              orgID={asset.orgID}
              selectedTemplate={selectedTemplate}
              selectedTimezone={selectedTimezone}
              templatesLoading={templatesLoading}
              timezone={timezone}
              vendor={vendor}
              visibility={viewing === 'basics' ? '' : 'd-none'}
            />
            <AssetAdvancedInfo
              handlePropUpdateSimple={handlePropUpdateSimple}
              stwWiFiControl={stwWiFiControl}
              stwWiFiEnabled={stwWiFiEnabled}
              WiFipassword={WiFipassword}
              WiFissid={WiFissid}
              vendor={vendor}
              visibility={viewing === 'advanced' ? '' : 'd-none'}
            />
            <p className="mb-0 text-right three-quarter-text text-danger">
              {error}
            </p>
            <div
              className={`flex-grow-0 d-${
                viewing === 'tag' ? 'none' : 'flex'
              } align-items-center justify-content-between justify-content-lg-end`}
            >
              <button
                className="btn btn-outline-secondary py-0"
                onClick={toggle}
              >
                Cancel
              </button>
              <button
                className="btn modal-button-submit py-0 ml-lg-3"
                type="submit"
              >
                {updateRequestPending ? (
                  <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </ModalBody>
      <ModalFooter className="d-lg-none align-items-center justify-content-around px-0 py-2">
        <button
          className="btn btn-link p-0 d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('basics')}
        >
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon
              className={`${
                viewing === 'basics'
                  ? 'text-brand-primary'
                  : 'text-secondary-gray'
              }`}
              icon={faCircle}
              size="lg"
            />
            <FontAwesomeIcon className="text-white" icon={faInfo} />
          </span>
          <span
            className={`text-nowrap ${
              viewing === 'basics'
                ? 'text-brand-primary'
                : 'text-icon-link-gray'
            } mt-1 three-quarter-text`}
          >
            Basics
          </span>
        </button>
        <button
          className="btn btn-link p-0 d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('advanced')}
        >
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon
              className={`${
                viewing === 'advanced'
                  ? 'text-brand-primary'
                  : 'text-secondary-gray'
              }`}
              icon={faCircle}
              size="lg"
            />
            <FontAwesomeIcon icon={faCog} color="white" />
          </span>
          <span
            className={`text-nowrap ${
              viewing === 'advanced'
                ? 'text-brand-primary'
                : 'text-icon-link-gray'
            } mt-1 three-quarter-text`}
          >
            Advanced
          </span>
        </button>
        <button
          className="btn btn-link p-0 d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('tag')}
        >
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon
              className={`${
                viewing === 'tag' ? 'text-brand-primary' : 'text-secondary-gray'
              }`}
              icon={faTag}
            />
          </span>
          <span
            className={`text-nowrap ${
              viewing === 'tag' ? 'text-brand-primary' : 'text-icon-link-gray'
            }
             mt-1 three-quarter-text`}
          >
            Tags
          </span>
        </button>
      </ModalFooter>
      <style jsx>{`
        .page-indicator {
          background-color: var(--brand-primary);
          color: var(--brand-primary);
          height: 25px;
          left: 0;
          width: 5px;
        }
        .view-link {
          color: var(--icon-link-gray);
          font-size: 0.5rem;
        }
        .view-link:hover {
          color: var(--text-muted);
        }
        .mobile-link-text {
          font-size: 0.75rem;
          font-weight: 400;
        }
        @media (min-width: 768px) {
          .modal-main-content-container {
            border-bottom-right-radius: 1rem;
          }
          .modal-side-nav-container {
            border-bottom-left-radius: 1rem;
          }
          .view-link {
            font-size: 1rem;
            margin-left: 0.25rem;
          }
        }
        @media (min-width: 992px) {
          .view-link {
            margin-left: 0.5rem;
          }
        }
      `}</style>
    </ModalLayout>
  );
};

EditAssetModalDisplay.propTypes = {
  asset: PropTypes.object,
  availableTemplates: PropTypes.arrayOf(PropTypes.object),
  commandAndControl: PropTypes.bool,
  currentTemplateID: PropTypes.string,
  error: PropTypes.string,
  handleCCToggle: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  handlePropUpdateSimple: PropTypes.func,
  handleTemplateSelect: PropTypes.func,
  handleTimezoneSelected: PropTypes.func,
  handleTimezoneToggle: PropTypes.func,
  isOpen: PropTypes.bool,
  selectedTemplate: PropTypes.object,
  selectedTimezone: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  startingNickname: PropTypes.string,
  submitUpdate: PropTypes.func,
  templatesLoading: PropTypes.bool,
  timezone: PropTypes.string,
  toggle: PropTypes.func,
  toggleView: PropTypes.func,
  updateRequestPending: PropTypes.bool,
  stwWiFiControl: PropTypes.bool,
  stwWiFiEnabled: PropTypes.bool,
  WiFissid: PropTypes.string,
  WiFipassword: PropTypes.string,
  vendor: PropTypes.string,
  viewing: PropTypes.string,
};

EditAssetModalDisplay.defaultProps = { asset: {} };
